import { useState, useEffect, useRef } from "react";
import { Plus, Pencil, Trash2, Music, Upload, Loader2, X, Play, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import AdminLayout from "@/components/layout/AdminLayout";
import { LocationInput } from "@/components/shared/LocationPickerV2";
import axios from "axios";
import { API } from "@/App";
import AdminPagination from "@/components/admin/AdminPagination";

const getImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http')) return url;
  const backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (url.startsWith('/uploads/')) {
    return `${backendUrl}/api${url}`;
  }
  return `${backendUrl}${url}`;
};

const GENRES = [
  "Música Popular",
  "Punta",
  "Reggaetón",
  "Cumbia",
  "Rock",
  "Balada",
  "Folclore",
  "Hip Hop",
  "Electrónica",
  "Jazz",
  "Pintura",
  "Escultura",
  "Danza",
  "Teatro",
  "Otro"
];

export default function AdminArtistas() {
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(25);
  const [total, setTotal] = useState(0);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedArtist, setSelectedArtist] = useState(null);
  const [saving, setSaving] = useState(false);
  const [mediaList, setMediaList] = useState([]);
  const [mediaDialogOpen, setMediaDialogOpen] = useState(false);
  const [mediaSelectMode, setMediaSelectMode] = useState("featured");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const [formData, setFormData] = useState({
    name: "",
    real_name: "",
    genre: "",
    bio: "",
    municipio: "",
    lat: null,
    lng: null,
    spotify_url: "",
    youtube_url: "",
    tiktok_url: "",
    instagram_url: "",
    facebook_url: "",
    contact_phone: "",
    contact_email: "",
    featured_image_id: "",
    featured_image_url: "",
    gallery_image_ids: [],
    gallery_urls: [],
    videos: [],
    status: "draft",
  });

  const token = localStorage.getItem("session_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchArtists();
  }, [page, limit]);

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${API}/artists?skip=${(page - 1) * limit}&limit=${limit}`, { headers });
      setArtists(response.data.items);
      setTotal(response.data.total || 0);
    } catch (error) {
      console.error("Error fetching artists:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchMedia = async () => {
    try {
      const response = await axios.get(`${API}/media?limit=100`, { headers });
      setMediaList(response.data.items);
    } catch (error) {
      console.error("Error fetching media:", error);
    }
  };

  const handleOpenDialog = (artist = null) => {
    if (artist) {
      setSelectedArtist(artist);
      setFormData({
        name: artist.name || "",
        real_name: artist.real_name || "",
        genre: artist.genre || "",
        bio: artist.bio || "",
        municipio: artist.municipio || "",
        lat: artist.lat || null,
        lng: artist.lng || null,
        spotify_url: artist.spotify_url || "",
        youtube_url: artist.youtube_url || "",
        tiktok_url: artist.tiktok_url || "",
        instagram_url: artist.instagram_url || "",
        facebook_url: artist.facebook_url || "",
        contact_phone: artist.contact_phone || "",
        contact_email: artist.contact_email || "",
        featured_image_id: artist.featured_image_id || "",
        featured_image_url: artist.featured_image_url || "",
        gallery_image_ids: artist.gallery_image_ids || [],
        gallery_urls: artist.gallery_urls || [],
        videos: artist.videos || [],
        status: artist.status || "draft",
      });
    } else {
      setSelectedArtist(null);
      setFormData({
        name: "", real_name: "", genre: "", bio: "", municipio: "",
        lat: null, lng: null,
        spotify_url: "", youtube_url: "", tiktok_url: "", instagram_url: "", facebook_url: "",
        contact_phone: "", contact_email: "",
        featured_image_id: "", featured_image_url: "",
        gallery_image_ids: [], gallery_urls: [], videos: [],
        status: "draft",
      });
    }
    setDialogOpen(true);
  };

  const handleOpenMediaDialog = (mode) => {
    setMediaSelectMode(mode);
    fetchMedia();
    setMediaDialogOpen(true);
  };

  const handleMediaSelect = (media) => {
    let imageUrl = media.url;
    if (imageUrl && imageUrl.startsWith('/uploads/')) {
      imageUrl = `${process.env.REACT_APP_BACKEND_URL}/api${imageUrl}`;
    }

    if (mediaSelectMode === "featured") {
      setFormData({
        ...formData,
        featured_image_id: media.media_id,
        featured_image_url: imageUrl,
      });
      setMediaDialogOpen(false);
    } else {
      if (!formData.gallery_image_ids.includes(media.media_id)) {
        setFormData({
          ...formData,
          gallery_image_ids: [...formData.gallery_image_ids, media.media_id],
          gallery_urls: [...formData.gallery_urls, { id: media.media_id, url: imageUrl }],
        });
      }
    }
  };

  const handleRemoveGalleryImage = (mediaId) => {
    setFormData({
      ...formData,
      gallery_image_ids: formData.gallery_image_ids.filter((id) => id !== mediaId),
      gallery_urls: formData.gallery_urls.filter((img) => img.id !== mediaId),
    });
  };

  const handleAddVideo = () => {
    setFormData({
      ...formData,
      videos: [...formData.videos, { title: "", youtube_url: "", description: "" }],
    });
  };

  const handleRemoveVideo = (index) => {
    const newVideos = [...formData.videos];
    newVideos.splice(index, 1);
    setFormData({ ...formData, videos: newVideos });
  };

  const handleVideoChange = (index, field, value) => {
    const newVideos = [...formData.videos];
    newVideos[index][field] = value;
    setFormData({ ...formData, videos: newVideos });
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formDataUpload = new FormData();
      formDataUpload.append('file', file);
      formDataUpload.append('title', file.name.replace(/\.[^/.]+$/, ''));

      const response = await axios.post(`${API}/media/upload`, formDataUpload, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' }
      });

      toast.success("Imagen subida");
      await fetchMedia();
      handleMediaSelect(response.data);
    } catch (error) {
      toast.error("Error al subir imagen");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      toast.error("El nombre es requerido");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        real_name: formData.real_name || null,
        genre: formData.genre || null,
        bio: formData.bio || null,
        municipio: formData.municipio || null,
        spotify_url: formData.spotify_url || null,
        youtube_url: formData.youtube_url || null,
        tiktok_url: formData.tiktok_url || null,
        instagram_url: formData.instagram_url || null,
        facebook_url: formData.facebook_url || null,
        contact_phone: formData.contact_phone || null,
        contact_email: formData.contact_email || null,
        featured_image_id: formData.featured_image_id || null,
      };

      if (selectedArtist) {
        await axios.put(`${API}/artists/${selectedArtist.artist_id}`, payload, { headers });
        toast.success("Artista actualizado");
      } else {
        await axios.post(`${API}/artists`, payload, { headers });
        toast.success("Artista creado");
      }

      setDialogOpen(false);
      fetchArtists();
    } catch (error) {
      toast.error("Error al guardar");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedArtist) return;
    try {
      await axios.delete(`${API}/artists/${selectedArtist.artist_id}`, { headers });
      toast.success("Artista eliminado");
      setDeleteDialogOpen(false);
      fetchArtists();
    } catch (error) {
      toast.error("Error al eliminar");
    }
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-brand-text">Artistas</h1>
            <p className="text-brand-textSecondary">Gestiona el directorio de artistas</p>
          </div>
          <Button onClick={() => handleOpenDialog()} data-testid="new-artist-btn">
            <Plus className="h-4 w-4 mr-2" />
            Nuevo Artista
          </Button>
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
                <div className="aspect-square bg-gray-200 rounded-lg mb-3" />
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-2" />
                <div className="h-4 bg-gray-200 rounded w-1/2" />
              </div>
            ))}
          </div>
        ) : artists.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {artists.map((artist) => (
              <div key={artist.artist_id} className="bg-white rounded-xl overflow-hidden shadow-sm border">
                <div className="aspect-square bg-gray-100 relative">
                  {artist.featured_image_url ? (
                    <img
                      src={getImageUrl(artist.featured_image_url)}
                      alt={artist.name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="h-16 w-16 text-gray-300" />
                    </div>
                  )}
                  {artist.genre && (
                    <span className="absolute top-2 left-2 px-2 py-1 bg-primary text-white text-xs rounded-full">
                      {artist.genre}
                    </span>
                  )}
                </div>
                <div className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <div>
                      <h3 className="font-semibold text-brand-text">{artist.name}</h3>
                      <p className="text-sm text-brand-textMuted">
                        {artist.status === "published" ? "Publicado" : "Borrador"}
                        {artist.municipio && ` • ${artist.municipio}`}
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="icon" onClick={() => handleOpenDialog(artist)}>
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => {
                          setSelectedArtist(artist);
                          setDeleteDialogOpen(true);
                        }}
                      >
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 bg-white rounded-xl">
            <Music className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-500">No hay artistas creados</p>
          </div>
        )}
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{selectedArtist ? "Editar Artista" : "Nuevo Artista"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Nombre artístico *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Nombre del artista"
                />
              </div>
              <div>
                <Label>Nombre real</Label>
                <Input
                  value={formData.real_name}
                  onChange={(e) => setFormData({ ...formData, real_name: e.target.value })}
                  placeholder="Nombre completo"
                />
              </div>
              <div>
                <Label>Género / Categoría</Label>
                <Select value={formData.genre} onValueChange={(v) => setFormData({ ...formData, genre: v })}>
                  <SelectTrigger><SelectValue placeholder="Seleccionar" /></SelectTrigger>
                  <SelectContent>
                    {GENRES.map((g) => <SelectItem key={g} value={g}>{g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Municipio</Label>
                <Input
                  value={formData.municipio}
                  onChange={(e) => setFormData({ ...formData, municipio: e.target.value })}
                  placeholder="Ej: Choluteca"
                />
              </div>
            </div>

            {/* Location on Map */}
            <LocationInput
              lat={formData.lat}
              lng={formData.lng}
              onChange={({ lat, lng }) => setFormData({ ...formData, lat, lng })}
              label="Ubicación en Mapa (opcional)"
            />

            <div>
              <Label>Biografía</Label>
              <Textarea
                value={formData.bio}
                onChange={(e) => setFormData({ ...formData, bio: e.target.value })}
                placeholder="Biografía del artista"
                rows={4}
              />
            </div>

            {/* Social Links */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Redes Sociales</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Spotify (URL del artista)</Label>
                  <Input
                    value={formData.spotify_url}
                    onChange={(e) => setFormData({ ...formData, spotify_url: e.target.value })}
                    placeholder="https://open.spotify.com/artist/..."
                  />
                </div>
                <div>
                  <Label>YouTube (canal)</Label>
                  <Input
                    value={formData.youtube_url}
                    onChange={(e) => setFormData({ ...formData, youtube_url: e.target.value })}
                    placeholder="https://youtube.com/@..."
                  />
                </div>
                <div>
                  <Label>Instagram</Label>
                  <Input
                    value={formData.instagram_url}
                    onChange={(e) => setFormData({ ...formData, instagram_url: e.target.value })}
                    placeholder="https://instagram.com/..."
                  />
                </div>
                <div>
                  <Label>Facebook</Label>
                  <Input
                    value={formData.facebook_url}
                    onChange={(e) => setFormData({ ...formData, facebook_url: e.target.value })}
                    placeholder="https://facebook.com/..."
                  />
                </div>
                <div>
                  <Label>TikTok</Label>
                  <Input
                    value={formData.tiktok_url}
                    onChange={(e) => setFormData({ ...formData, tiktok_url: e.target.value })}
                    placeholder="https://tiktok.com/@..."
                  />
                </div>
              </div>
            </div>

            {/* Contact */}
            <div className="border-t pt-4">
              <h4 className="font-medium mb-3">Contacto / Contrataciones</h4>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Teléfono</Label>
                  <Input
                    value={formData.contact_phone}
                    onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                    placeholder="+504 0000-0000"
                  />
                </div>
                <div>
                  <Label>Email</Label>
                  <Input
                    value={formData.contact_email}
                    onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                    placeholder="contacto@artista.com"
                  />
                </div>
              </div>
            </div>

            {/* Featured Image */}
            <div className="border-t pt-4">
              <Label>Foto Principal</Label>
              {formData.featured_image_url ? (
                <div className="relative mt-2 w-48">
                  <img
                    src={getImageUrl(formData.featured_image_url)}
                    alt="Featured"
                    className="w-full aspect-square object-cover rounded-lg"
                  />
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="absolute bottom-2 right-2"
                    onClick={() => handleOpenMediaDialog("featured")}
                  >
                    Cambiar
                  </Button>
                </div>
              ) : (
                <Button
                  type="button"
                  variant="outline"
                  className="mt-2"
                  onClick={() => handleOpenMediaDialog("featured")}
                >
                  Seleccionar imagen
                </Button>
              )}
            </div>

            {/* Gallery */}
            <div className="border-t pt-4">
              <Label>Galería ({formData.gallery_urls.length} fotos)</Label>
              <div className="mt-2 grid grid-cols-6 gap-2">
                {formData.gallery_urls.map((img) => (
                  <div key={img.id} className="relative aspect-square">
                    <img src={getImageUrl(img.url)} alt="" className="w-full h-full object-cover rounded" />
                    <button
                      type="button"
                      onClick={() => handleRemoveGalleryImage(img.id)}
                      className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
                <button
                  type="button"
                  onClick={() => handleOpenMediaDialog("gallery")}
                  className="aspect-square border-2 border-dashed rounded flex items-center justify-center text-gray-400 hover:border-primary hover:text-primary"
                >
                  <Plus className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Videos */}
            <div className="border-t pt-4">
              <div className="flex items-center justify-between mb-3">
                <Label>Videos Destacados ({formData.videos.length})</Label>
                <Button type="button" variant="outline" size="sm" onClick={handleAddVideo}>
                  <Plus className="h-4 w-4 mr-1" /> Video
                </Button>
              </div>
              {formData.videos.map((video, index) => (
                <div key={index} className="bg-gray-50 rounded-lg p-3 mb-2">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Video {index + 1}</span>
                    <button
                      type="button"
                      onClick={() => handleRemoveVideo(index)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <X className="h-4 w-4" />
                    </button>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    <Input
                      value={video.title}
                      onChange={(e) => handleVideoChange(index, "title", e.target.value)}
                      placeholder="Título del video"
                    />
                    <Input
                      value={video.youtube_url}
                      onChange={(e) => handleVideoChange(index, "youtube_url", e.target.value)}
                      placeholder="https://youtube.com/watch?v=..."
                    />
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t pt-4">
              <Label>Estado</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="draft">Borrador</SelectItem>
                  <SelectItem value="published">Publicado</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
              <Button type="submit" disabled={saving}>{saving ? "Guardando..." : "Guardar"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {/* Media Dialog */}
      <Dialog open={mediaDialogOpen} onOpenChange={setMediaDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Seleccionar imagen</DialogTitle>
          </DialogHeader>
          <div className="border-2 border-dashed rounded-lg p-4 text-center mb-4">
            <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileUpload} className="hidden" id="artist-upload" />
            <label htmlFor="artist-upload" className="cursor-pointer">
              {uploading ? <Loader2 className="h-8 w-8 mx-auto animate-spin" /> : (
                <>
                  <Upload className="h-8 w-8 mx-auto text-gray-400 mb-2" />
                  <span className="text-sm text-gray-600">Subir nueva imagen</span>
                </>
              )}
            </label>
          </div>
          <div className="grid grid-cols-5 gap-3">
            {mediaList.map((media) => (
              <button
                key={media.media_id}
                type="button"
                onClick={() => handleMediaSelect(media)}
                className="aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-primary"
              >
                <img src={getImageUrl(media.url)} alt="" className="w-full h-full object-cover" />
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar artista?</AlertDialogTitle>
            <AlertDialogDescription>Esta acción no se puede deshacer.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-500 hover:bg-red-600">Eliminar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
        <AdminPagination
          page={page}
          total={total}
          limit={limit}
          onPageChange={setPage}
          onLimitChange={(l) => { setLimit(l); setPage(1); }}
          testId="artistas-pagination"
          label="artistas"
        />
    </AdminLayout>
  );
}
